import { Fragment } from "react";
import Link from "next/link";
import { PortableText } from "next-sanity";
import { PortableTextBlock } from "sanity";

import { PageHomeWhatSetsApartCard } from "@/sanity/pages/PageHomeTemplate";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { FeatureCardIcon } from "@/components/pageComponents/global/FeatureCard";
import Image from "next/image";
import stars from "@/public/global/stars.png";

interface IWhatSetsApartSectionData {
  whatSetsApartChip?: string;
  whatSetsApartTitle?: PortableTextBlock[];
  whatSetsApartCards?: PageHomeWhatSetsApartCard[];
  whatSetsApartTrustpilotTitle?: string;
  whatSetsApartStats?: Array<{ _key?: string; value?: string; label?: string }>;
  whatSetsApartCtaLabel?: string;
  whatSetsApartCtaUrl?: string;
}

function TrustpilotStars({ className }: { className?: string }) {
  return (
    // <svg
    //   className={className}
    //   xmlns="http://www.w3.org/2000/svg"
    //   width="122"
    //   height="23"
    //   fill="none"
    //   viewBox="0 0 122 23"
    // >
    //   <g clipPath="url(#clip_wsa)">
    //     <path
    //       fill="#219653"
    //       d="M22.875 0H0v22.875h22.875zM47.656 0H24.781v22.875h22.875zM72.438 0H49.561v22.875h22.876zM97.219 0H74.344v22.875h22.875zM122 0H99.125v22.875H122z"
    //     />
    //     <path
    //       fill="#fff"
    //       d="m11.437 15.417 3.48-.882 1.453 4.48zm8.007-5.79H13.32L11.437 3.86 9.555 9.626H3.43l4.956 3.575-1.882 5.766 4.956-3.574 3.05-2.192zM36.219 15.417l3.478-.882 1.454 4.48zm8.006-5.79H38.1L36.218 3.86l-1.882 5.766h-6.124l4.957 3.575-1.883 5.766 4.956-3.574 3.05-2.192zM61 15.417l3.479-.882 1.453 4.48zm8.006-5.79h-6.124L61 3.86l-1.883 5.766h-6.124l4.957 3.575-1.883 5.766 4.956-3.574 3.05-2.192zM85.78 15.417l3.48-.882 1.453 4.48zm8.007-5.79h-6.124L85.781 3.86l-1.883 5.766h-6.123l4.956 3.575-1.882 5.766 4.956-3.574 3.05-2.192zM110.562 15.417l3.479-.882 1.454 4.48zm8.006-5.79h-6.123l-1.883-5.767-1.882 5.766h-6.124l4.956 3.575-1.882 5.766 4.956-3.574 3.05-2.192z"
    //     />
    //   </g>
    //   <defs>
    //     <clipPath id="clip_wsa">
    //       <path fill="#fff" d="M0 0h122v22.875H0z" />
    //     </clipPath>
    //   </defs>
    // </svg>

    <div className="h-auto w-[12.5rem] shrink-0">
      <Image
        className="!h-full !w-full !object-contain"
        alt="Stars"
        src={stars}
      />
    </div>
  );
}

function FeatureCard({ card }: { card: PageHomeWhatSetsApartCard }) {
  return (
    <article className="flex flex-col items-start gap-[8rem] border border-[rgba(47,47,47,0.1)] bg-[#0d2922] p-[4rem] lg:h-[44.25rem]">
      {/* Icon box */}
      <div className="relative grid h-[7.25rem] w-[7.25rem] shrink-0 place-items-center rounded-[0.25rem] border border-[rgba(255,255,255,0.1)]">
        <div className="absolute inset-[0.75rem] rounded-[0.25rem] bg-[rgba(255,255,255,0.05)]" />
        <FeatureCardIcon
          icon={card.icon}
          className="relative z-10 text-white"
        />
      </div>

      {/* Body richtext: heading + paragraph */}
      {card.body && (
        <div className="richtext richtext-start flex w-full flex-col gap-[1.75rem] text-white prose-p:mb-0 prose-p:font-geist prose-p:text-[2.25rem] prose-p:leading-[1.33] prose-p:tracking-[-0.09rem] prose-p:text-white/65 prose-p:text-white/70 prose-headings:mt-0 prose-headings:mb-0 prose-headings:font-geist prose-headings:text-[2.75rem] prose-headings:leading-[1.3] prose-headings:font-medium prose-headings:tracking-[-0.0825rem] prose-headings:text-white">
          <PortableText
            value={card.body}
            components={richTextComponent}
          />
        </div>
      )}
    </article>
  );
}

export default function WhatSetsApartSection({ data }: { data: IWhatSetsApartSectionData }) {
  const cards = data.whatSetsApartCards ?? [];
  const stats = data.whatSetsApartStats ?? [];

  if (!data.whatSetsApartTitle && !cards.length) return null;

  const hasFooter = !!(data.whatSetsApartTrustpilotTitle || stats.length > 0);

  return (
    <section className="w-full bg-white pt-[8rem] lg:pt-[18rem]">
      {/* Header */}
      <div className="mx-auto flex w-[min(86rem,calc(100%-4rem))] flex-col items-center gap-[2rem] pb-[4rem] text-center lg:pb-[7.87rem]">
        {data.whatSetsApartChip && (
          <p className="mb-0 font-blacker text-[2rem] leading-[1.5] text-black italic">{data.whatSetsApartChip}</p>
        )}
        {data.whatSetsApartTitle && (
          <div className="richtext flex w-full flex-col gap-[2rem] prose-p:max-w-[60rem] prose-p:text-[2rem] prose-p:leading-[1.5] prose-p:text-black/60 prose-em:font-blacker prose-em:whitespace-nowrap">
            <PortableText
              value={data.whatSetsApartTitle}
              components={richTextComponent}
            />
          </div>
        )}
      </div>

      {/* Cards grid */}
      <div
        className={`mx-auto w-[min(150rem,calc(100%-4rem))] ${hasFooter ? "pb-[10rem] lg:pb-[18rem]" : "pb-[10rem]"}`}>
        <div className="grid grid-cols-1 gap-[3.25rem] lg:grid-cols-3">
          {cards.map((card) => (
            <FeatureCard
              key={card._key}
              card={card}
            />
          ))}
        </div>
      </div>

      {/* Trustpilot footer */}
      {hasFooter && (
        <div className="">
          <div className="mx-auto w-[min(140rem,calc(100%-4rem))] pb-[8rem] lg:pb-[15rem]">
            {/* Mobile */}
            <div className="flex flex-col items-center gap-[4rem] lg:hidden">
              <div className="flex w-full flex-col items-center gap-[2rem] text-center">
                <TrustpilotStars className="h-auto w-[15.25rem]" />
                {data.whatSetsApartTrustpilotTitle && (
                  <p className="mb-0 font-geist text-[3.5rem] leading-[1.3] font-medium tracking-[-0.175rem] text-black">
                    {data.whatSetsApartTrustpilotTitle}
                  </p>
                )}
              </div>

              {stats.length > 0 && (
                <div className="w-full divide-y divide-[rgba(0,27,20,0.1)] px-[3.5rem]">
                  {stats.map((stat) => (
                    <div
                      key={stat._key}
                      className="flex flex-col gap-[1rem] py-[3rem] first:pt-0 last:pb-0">
                      {stat.value && (
                        <p className="mb-0 font-geist text-[2.75rem] leading-none font-medium tracking-[-0.1375rem] text-[#003828]">
                          {stat.value}
                        </p>
                      )}
                      {stat.label && (
                        <p className="mb-0 font-geist text-[1.75rem] leading-[1.5] text-black/60">{stat.label}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Desktop — exact Figma layout */}
            <div className="hidden lg:flex lg:h-[13.375rem] lg:items-center lg:gap-[8rem]">
              {/* Left: stars top, heading + CTA bottom */}
              <div className="flex flex-col gap-[2rem]">
                <TrustpilotStars className="h-auto w-[15.25rem] shrink-0" />
                <div className="flex flex-col gap-[1.25rem]">
                  {data.whatSetsApartTrustpilotTitle && (
                    <p className="mb-0 w-[23.125rem] font-geist text-[3.5rem] leading-[1.3] font-medium tracking-[-0.175rem] text-black">
                      {data.whatSetsApartTrustpilotTitle}
                    </p>
                  )}
                </div>
              </div>

              {/* Right: stats with vertical dividers */}
              {stats.length > 0 && (
                <div className="flex h-full flex-1 items-start justify-between">
                  {stats.map((stat, i) => (
                    <Fragment key={stat._key}>
                      {i > 0 && <div className="h-full w-px shrink-0 bg-[rgba(0,27,20,0.1)]" />}
                      <div className="flex max-w-[20rem] flex-col justify-between gap-[2rem]">
                        {stat.value && (
                          <p className="mb-0 font-geist text-[2.75rem] leading-none font-medium tracking-[-0.14rem] text-[#003828]">
                            {stat.value}
                          </p>
                        )}
                        {stat.label && (
                          <p className="mb-0 max-w-[24.5rem] font-geist text-[1.75rem] leading-[1.5] text-black/60">
                            {stat.label}
                          </p>
                        )}
                      </div>
                    </Fragment>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
